import React, { useState } from 'react';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { FileDownloadOutlined, GridOnOutlined, KeyboardArrowDown, PictureAsPdfOutlined } from '@mui/icons-material';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import BusyIcon from './BusyIcon';

export type ExportKind = 'xlsx' | 'pdf';

interface Props {
  /** How many rows the export holds — every one matching the filters, not just this page. */
  count: number;
  /** Plural, lower case — "applications", "enquiries". */
  noun: string;
  /** Filters are narrowing the list, so the menu says "matching". */
  filtered: boolean;
  /** Builds and downloads the file; reports its own errors. */
  onExport: (kind: ExportKind) => Promise<void>;
  /** Medium beside a page's main button; small in a toolbar. */
  size?: 'small' | 'medium';
}

/**
 * The Export button for a list: Excel with every detail, or a printable PDF,
 * of everything the filters match. Says "Exporting…" while the file is built.
 */
const ExportMenuButton: React.FC<Props> = ({ count, noun, filtered, onExport, size = 'small' }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const what = `${count} ${filtered ? 'matching ' : ''}${noun}`;

  const run = async (kind: ExportKind) => {
    setAnchor(null);
    setBusy(true);
    try {
      await onExport(kind);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant="outlined"
        startIcon={<BusyIcon busy={busy} size={14}><FileDownloadOutlined fontSize="small" /></BusyIcon>}
        endIcon={<KeyboardArrowDown fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={busy || !count}
        sx={{ textTransform: 'none' }}
      >
        {busy ? 'Exporting…' : 'Export'}
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        {...elegantMenuWithHintsProps}
      >
        <MenuItem onClick={() => run('xlsx')}>
          <ListItemIcon><GridOnOutlined fontSize="small" sx={{ color: 'success.main' }} /></ListItemIcon>
          <ListItemText primary="Excel (.xlsx)" secondary={`All ${what} · every detail`} />
        </MenuItem>
        <MenuItem onClick={() => run('pdf')}>
          <ListItemIcon><PictureAsPdfOutlined fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText primary="PDF" secondary={`Printable register of the ${what}`} />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ExportMenuButton;
